import { categorize } from './miss_category.js';
import { AUTOMATION_USER } from './status.js';
import { failureReason } from './reasons.js';
import { neverWentOut } from './unsent.js';
// Plain words (Kimi's UI assessment, Rod 2026-10-13 "1 a", change 7): the system's own lane names ("Sweeper, no click", "Litify note lane",
// "Controller re-fire (asklast recheck)", "Ins Creation (new record)") stay in SOURCE_DETAIL and each row's hover, for engineers.
export const SOURCE_LABEL = {
    sweeper_auto: 'Found in the case files, called automatically', sweeper_click: 'Found in the case files, approved by a person',
    router_click: 'New document (police report, dec page, card or case note)', controller: 'Automatic re-calls', ins_creation: 'New insurance records',
    staff: 'Staff, by hand', other_auto: 'Other automation', automation_unsplit: 'Automation (which kind is not readable right now)',
};
export const SOURCE_DETAIL = {
    sweeper_auto: 'Sweeper (insurance_scan), no click', sweeper_click: 'Sweeper (insurance_scan), after a click', router_click: 'Router lanes: police report / dec page / card / Litify note',
    controller: 'Claims controller re-fires', ins_creation: 'Ins Creation (new records)', staff: 'Staff, by hand', other_auto: 'Other automation',
    automation_unsplit: 'Automation (router or Ins Creation; router ledger unreadable)',
};
const ORDER = ['sweeper_auto', 'sweeper_click', 'router_click', 'controller', 'ins_creation', 'automation_unsplit', 'other_auto', 'staff'];
const LANE = { tcr: 'Police report', dec_page: 'Dec page', card: 'Insurance card', litify_note: 'Litify note', insurance_scan: 'Sweeper' };
const LANE_PLAIN = { tcr: 'Police report', dec_page: 'Dec page', card: 'Insurance card', litify_note: 'Case note', insurance_scan: 'Case files' };
const RESULT_TEXT = {
    success: 'Got a claim number', running: 'Calling or queued', waiting: 'Waiting for the 7 am start', never_ran: 'No call and no result yet',
    stopped: 'Stopped before dialing: the record already had a claim number',
};
const RUNNING_MIN = 75;
// A call log row with no result yet means a live call only inside the call's own limit (60 min, plus margin): the Claims Agent writes the
// row before its duplicate guard, so a request the guard stopped keeps a result-less row for ever (10-08 CAN-24273).
const CALL_LIVE_MIN = 65;
const ms = (s) => Date.parse(s.replace(/\+0000$/, 'Z'));
const id15 = (s) => (s ?? '').slice(0, 15);
/** 07:00 Pacific on the Pacific day of `now`, as epoch ms (the Claims Agent and the controller start dialing then). */
function sevenAmPt(now) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(now).map((x) => [x.type, x.value]));
    for (const off of [7, 8]) {
        const t = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 7 + off, 0, 0);
        const h = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', hourCycle: 'h23' }).format(new Date(t));
        if (Number(h) === 7)
            return t;
    }
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 14, 0, 0);
}
/** The band's numbers for a list of requests: every request, or (the State row, states.ts) the requests of one state. */
export function activityCounts(requests) {
    const placed = (r) => r.dialed === true;
    const stopped = (r) => r.dialed === false;
    const bySource = ORDER.map((s) => {
        const rs = requests.filter((r) => r.source === s);
        return { source: s, label: SOURCE_LABEL[s], requests: rs.length, placed: rs.filter(placed).length, claims: rs.filter((r) => r.claimOpened).length, stopped: rs.filter(stopped).length };
    }).filter((g) => g.requests > 0 || g.source === 'sweeper_auto' || g.source === 'staff');
    const totals = { requests: requests.length, placed: requests.filter(placed).length, claims: requests.filter((r) => r.claimOpened).length,
        stopped: requests.filter(stopped).length, onlineHeld: requests.filter((r) => r.category === 'ONLINE_HELD').length,
        blocked: requests.filter((r) => r.category === 'BLOCKED').length };
    return { bySource, totals };
}
export function buildActivity(input) {
    const nowMs = input.now.getTime();
    const seven = sevenAmPt(input.now);
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(input.now);
    // A past day is over: today's 7 am hold and first-75-minute "calling" window are not its clock (input.day.label is display text, not a test).
    const isToday = !input.day || input.day.day === today;
    /** when the request could first be dialed: today, not before 7 am; a past day's request, when it was made */
    const from = (c) => (isToday ? Math.max(ms(c.CreatedDate), seven) : ms(c.CreatedDate));
    const byController = new Map((input.actions ?? []).filter((a) => a.created_can_id).map((a) => [id15(a.created_can_id), a]));
    const byRearm = new Map((input.rearm ?? []).filter((r) => r.can_id).map((r) => [id15(r.can_id), r]));
    const tokenFor = new Map();
    for (const t of input.tokens ?? [])
        if (t.action === 'dispatch' && t.used_at)
            tokenFor.set(t.rearm_id, t);
    const calling = new Set(input.calling.map(id15));
    const dialedSet = input.dialed ? new Set(input.dialed.map(id15)) : null;
    const live = (c) => calling.has(id15(c.Id)) && nowMs - from(c) < CALL_LIVE_MIN * 60000;
    const sorted = [...input.cans].sort((a, b) => ms(a.CreatedDate) - ms(b.CreatedDate));
    const routerSplit = input.rearm !== null;
    const requests = sorted.map((c, i) => {
        const ins = c.Insurance__c ? input.insurance[c.Insurance__c] ?? input.insurance[id15(c.Insurance__c)] ?? null : null;
        let source;
        let startedBy;
        let startedByDetail;
        const ctl = byController.get(id15(c.Id));
        const rr = byRearm.get(id15(c.Id));
        if (c.CreatedById && c.CreatedById !== AUTOMATION_USER) {
            source = 'staff';
            startedBy = `${input.users[c.CreatedById] ?? 'A staff member'} (manual)`;
        }
        else if (ctl) {
            source = 'controller';
            startedBy = 'Automatic re-call';
            startedByDetail = `Controller re-fire${ctl.reason ? ` (${ctl.reason.toLowerCase().replace(/_/g, ' ')})` : ''}`;
        }
        else if (rr) {
            const tok = tokenFor.get(rr.id);
            const who = tok?.used_by ?? rr.approved_by;
            const lane = LANE[rr.source ?? ''] ?? 'Router';
            const plain = LANE_PLAIN[rr.source ?? ''] ?? 'New document';
            if (rr.source === 'insurance_scan') {
                source = who ? 'sweeper_click' : 'sweeper_auto';
                startedBy = who ? `Found in the case files, approved by ${who}` : 'Found in the case files, called automatically';
                startedByDetail = who ? `Sweeper, approved by ${who}` : 'Sweeper, no click';
            }
            else {
                source = 'router_click';
                startedBy = who ? `${plain}, approved by ${who}` : `${plain}, called automatically`;
                startedByDetail = who ? `${lane}, approved by ${who}` : `${lane} lane`;
            }
        }
        else if (!routerSplit) {
            source = 'automation_unsplit';
            startedBy = SOURCE_LABEL.automation_unsplit;
        }
        else if (ins && ins.createdById === AUTOMATION_USER && ms(c.CreatedDate) - ms(ins.createdDate) <= 48 * 3600000 && ms(c.CreatedDate) >= ms(ins.createdDate)) {
            source = 'ins_creation';
            startedBy = 'New insurance record';
            startedByDetail = 'Ins Creation (new record)';
        }
        else {
            source = 'other_auto';
            startedBy = 'Other automation';
        }
        const earlier = sorted.slice(0, i).filter((p) => p.Insurance__c && p.Insurance__c === c.Insurance__c);
        const prev = earlier[earlier.length - 1];
        const gapMin = prev ? Math.floor((ms(c.CreatedDate) - ms(prev.CreatedDate)) / 60000) : null;
        const repeatOfMinutes = gapMin !== null && gapMin <= 60 ? gapMin : null;
        let result;
        let resultText;
        if (c.Disposition__c === 'Success') {
            result = 'success';
            resultText = RESULT_TEXT.success;
        }
        else if (c.Disposition__c === 'Miss') {
            result = 'miss';
            resultText = categorize(c, { dialed: dialedSet ? dialedSet.has(id15(c.Id)) : null }).text;
        }
        else if (live(c)) {
            result = 'running';
            resultText = RESULT_TEXT.running;
        }
        else if (isToday && nowMs < seven) {
            result = 'waiting';
            resultText = RESULT_TEXT.waiting;
        }
        else if (isToday && nowMs - from(c) < RUNNING_MIN * 60000) {
            result = 'running';
            resultText = RESULT_TEXT.running;
        }
        else if (ins?.claim && earlier.some((p) => p.Disposition__c === 'Success')) {
            result = 'stopped';
            resultText = RESULT_TEXT.stopped;
        }
        else {
            result = 'never_ran';
            resultText = RESULT_TEXT.never_ran;
        }
        const claimOpened = result === 'success' && !!(c.Claim_Number__c || ins?.claim);
        // Dial evidence (Cesar 10-09: "calls placed does not have dial evidence"): a Retell call id when readable, else what the reason says.
        const cat = categorize(c, { dialed: dialedSet ? dialedSet.has(id15(c.Id)) : null, nowMs });
        const dialed = result === 'success' ? true
            : result === 'miss' ? (cat.group === 'NOT_DIALED' ? false : dialedSet ? (dialedSet.has(id15(c.Id)) || cat.key !== 'UNKNOWN') : true)
                : live(c) ? true : (result === 'never_ran' || result === 'stopped') ? (dialedSet ? dialedSet.has(id15(c.Id)) : null) : null;
        const s = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());
        const toldKeys = ['Policy_Number__c', 'Name_of_Insured__c', 'Insured_Phone__c', 'VIN__c', 'License_Plate__c', 'Incident_Date__c', 'Incident_Location__c', 'Phone__c', 'Client_Date_of_Birth__c'];
        const told = toldKeys.some((k) => Object.prototype.hasOwnProperty.call(c, k))
            ? { policy: s(c.Policy_Number__c), insured: s(c.Name_of_Insured__c), insuredPhone: s(c.Insured_Phone__c), vin: s(c.VIN__c), plate: s(c.License_Plate__c),
                dol: s(c.Incident_Date__c), location: s(c.Incident_Location__c), dialed: s(c.Phone__c), dob: s(c.Client_Date_of_Birth__c) }
            : null;
        const rec = s(c.Call_Recording_Url__c);
        const missReason = s(c.Miss_Reason__c);
        const reason = c.Disposition__c === 'Miss' && cat.group === 'DIALED' ? failureReason(c) : null;
        return {
            canId: c.Id, name: c.Name, at: c.CreatedDate, matterId: c.Matter__c, matter: c.Matter__c ? input.matters[c.Matter__c] ?? null : null,
            insuranceId: c.Insurance__c, side: ins?.side ?? (c.Claim_Type__c?.startsWith('First') ? 'PLT' : c.Claim_Type__c ? 'DEF' : null),
            carrier: ins?.carrier ?? null, source, startedBy, ...(startedByDetail ? { startedByDetail } : {}), result, resultText, claimOpened, repeatOfMinutes, dialed, category: cat.key,
            missReason, reason, notSent: neverWentOut(c), told, recordingUrl: rec && /^https:\/\//i.test(rec) ? rec : null,
        };
    });
    const { bySource, totals } = activityCounts(requests);
    let cardsWaiting = null;
    if (input.rearm) {
        const waiting = input.rearm.filter((r) => r.state === 'report_only' && r.slack_posted_at);
        cardsWaiting = { sweeper: waiting.filter((r) => r.source === 'insurance_scan').length, other: waiting.filter((r) => r.source !== 'insurance_scan').length };
    }
    const { day, label, note } = input.day ?? { day: today, label: 'Today', note: null };
    return { requests: requests.reverse(), bySource, totals, routerSplit, cardsWaiting, day, label, note };
}
//# sourceMappingURL=activity.js.map